"use client";

import React, { useState, useRef } from "react";
import styled from "styled-components";

import Box from "../Box";
import Flex from "../Flex";
import Text from "../Text";
import ImageView from "../ImageView";
import Icon from "../Icon";
import Button from "../Button";
import PopupMenu from "../PopupMenu";

import pluralize from "../../helpers/pluralize";
import { PrivacyLevel } from "../../constants/types";

import { Portal, Space } from "../../models";

import { getColor } from "../utils";

type Props = {
  id: string | number;
  portal: Portal;
  space: { access?: Pick<Space["access"], "permissions"> };
  onClick?: (portal: Pick<Portal, "privacy">) => void;
  fixedWidth?: boolean;

  /**
   * If provided, the overflow button is only displayed and the menuItems
   * are presented in a PopupMenu on click.
   *
   * This should be a node to populate a PopupMenu (i.e. [PopupMenu.Item])
   */
  menuItems?: React.ReactNode;
  identifier?: string;
};

type WrapperProps = {
  fixedWidth?: boolean;
};

export const PortalCardWrapper = styled(Box)<WrapperProps>`
  flex: 1 0 calc((1200px - 32px) / 3);
  max-width: ${props => (props.fixedWidth ? "389px" : "100%")};;
  width: ${props => (props.fixedWidth ? "389px" : "100%")};
  height: 100%;
  cursor: pointer;
  background-color: ${getColor("white")};
  transition: box-shadow 0.3s ease;
  border: 1px solid ${getColor("grayLight")};
  border-radius: 8px;
  padding: 16px;
  &:hover {
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.07),
    0px 1.541px 5.096px 0px rgba(0, 0, 0, 0.04),
    0px 0.326px 1.304px 0px rgba(0, 0, 0, 0.03);
  }
   @media screen and (max-width: 768px) {
    flex 1 0 calc((1088px - 32px) / 3);
    width: ${props => (props.fixedWidth ? "352px" : "100%")};
  }
    @media screen and (max-width: 560px) {
    flex 1 0 calc((992px - 32px) / 3);
    width: ${props => (props.fixedWidth ? "320px" : "100%")};
    }
`;

export const PortalText = styled(Text).attrs({})`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const iconMap = {
  [PrivacyLevel.public]: "globe",
  [PrivacyLevel.private]: "users",
  [PrivacyLevel.password]: "globe-lock",
};

/**
 *
 * Renders a portal card including an image, name, and description.
 *
 * The button on the card are shown based on the provided props:
 * Overflow button: menuItems
 * @param {Object} props
 */
const PortalCard: React.FC<Props> = ({
  id,
  portal,
  space,
  onClick,
  menuItems,
  identifier,
  fixedWidth,
}) => {
  const dragRef = useRef(null);

  const spacePermissions = ((space || {}).access || {}).permissions || [],
    canViewMembers = spacePermissions.includes("view_members");

  const [menuVisible, setMenuVisible] = useState(false);
  const menuId = identifier
    ? `${portal.id}-${identifier}-overflow-menu`
    : `${portal.id}-overflow-menu`;
  const menu =
    menuVisible && menuItems ? (
      <PopupMenu
        source={menuId}
        data-testid="portal-card-menu"
        close={() => setMenuVisible(!menuVisible)}>
        {menuItems}
      </PopupMenu>
    ) : null;

  const handleClick = () => {
    onClick(portal);
  };

  function renderOverflowButton() {
    return (
      <Button
        id="portal-overflow-button"
        data-popup-source={menuId}
        buttonStyle="tertiary"
        icon="overflow"
        width="24"
        height="24"
        onClick={e => {
          e.stopPropagation();
          setMenuVisible(!menuVisible);
        }}
        themeOverrides={{ primaryColor: "black" }}
      />
    );
  }

  function renderKitCounts() {
    return (
      <Flex justifyContent="center" alignItems="center" gap="2px">
        <Text id="kit-count" my="none" p="none" font="ui.small" color="grayDarker">
          {portal.counts.kits}
          {pluralize(" kit", portal.counts.kits)}
        </Text>
      </Flex>
    );
  }

  function renderMemberCount() {
    return (
      <Flex justifyContent="center" alignItems="center" gap="2px">
        <Text id="member-count" my="none" p="none" font="ui.small" color="grayDarker">
          {portal.counts.members}
          {pluralize(" user", portal.counts.members)}
        </Text>
      </Flex>
    );
  }

  function renderDescription() {
    return (
      <PortalText id="portal-description" mt="xs">
        {portal.description}
      </PortalText>
    );
  }
  return (
    <PortalCardWrapper
      ref={dragRef}
      data-testid={id}
      data-portalid={id}
      fixedWidth={fixedWidth}
      onClick={handleClick}>
      <Box width="100%">
        <ImageView
          css={{ cursor: onClick ? "pointer" : null }}
          src={portal.images.cover}
          icon="content.portal"
          alt={portal.name}
          aspectRatio={34}
          cover
          background="grayLighter"
          borderRadius="small"
        />
      </Box>
      <Box mt="s">
        <Flex justifyContent="space-between">
          <Text as="h2" id="portal-title" font="ui.regularSemiBold" truncate onClick={handleClick}>
            {portal.name}
          </Text>
          <Flex>
            <Icon id={`privacy-${portal.privacy}`} iconId={iconMap[portal.privacy]} size="24" />
            {menuItems && renderOverflowButton()}
            {menu}
          </Flex>
        </Flex>
        <Flex gap="4px">
          {renderKitCounts()}
          {canViewMembers && (
            <>
              <Text my="none" p="none" font="ui.small" color="grayLight">
                /
              </Text>
              {renderMemberCount()}
            </>
          )}
        </Flex>
        {portal.description && renderDescription()}
      </Box>
    </PortalCardWrapper>
  );
};

export default PortalCard;
